import { useMemo, useState } from "react";
import { Check, FileDown, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getGuest } from "@/lib/guest";
import GuestDetailsDialog from "@/components/site/GuestDetailsDialog";

const inr = (n) => "₹" + Math.round(Number(n) || 0).toLocaleString("en-IN");
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

export default function DecorCalculator({ d, slug }) {
  const basePrice = num(d.base_price);
  const includes = (d.base_includes || []).filter((s) => String(s).trim());
  const addons = useMemo(() => (d.addons || []).filter((a) => a && a.name && num(a.price) > 0), [d.addons]);
  const [qty, setQty] = useState({});
  const [guestOpen, setGuestOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const bump = (i, delta) => setQty((q) => ({ ...q, [i]: Math.min(Math.max((q[i] || 0) + delta, 0), 99) }));
  const selected = addons.map((a, i) => ({ ...a, qty: qty[i] || 0, amount: (qty[i] || 0) * num(a.price) })).filter((a) => a.qty > 0);
  const total = basePrice + selected.reduce((s, a) => s + a.amount, 0);

  const requestDownload = () => {
    const g = getGuest();
    if (g) downloadPdf(g);
    else setGuestOpen(true);
  };
  const downloadPdf = async (guest) => {
    setDownloading(true);
    try {
      const res = await api.post(
        "/public/decor-pdf",
        {
          property_slug: slug || "",
          base_label: d.base_label || "",
          base_price: basePrice,
          base_includes: includes,
          addons: selected.map((a) => ({ name: a.name, price: num(a.price), unit: a.unit || "", qty: a.qty })),
          disclaimer: d.disclaimer || "",
          name: guest?.name || "",
          phone: guest?.phone || "",
          email: guest?.email || "",
        },
        { responseType: "blob", timeout: 60000 }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Happy-Retreats-Decor-Estimate${slug ? `-${slug}` : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your decor estimate is downloading");
    } catch {
      toast.error("Could not prepare the decor PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start" data-testid="decor-calc">
      <div className="lg:col-span-7 space-y-8">
        <div className="border border-ink/10 bg-white">
          <div className="flex items-start justify-between gap-4 p-6 md:p-8 border-b border-ink/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] accent-color font-medium">Included · Base package</div>
              <div className="font-display text-2xl md:text-3xl mt-2">{d.base_label || "Signature decor package"}</div>
            </div>
            <div className="font-display text-2xl md:text-3xl whitespace-nowrap" data-testid="decor-base-price">{inr(basePrice)}</div>
          </div>
          {includes.length > 0 && (
            <div className="p-6 md:p-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {includes.map((inc, i) => (
                <div key={i} className="flex items-start gap-3 text-sm font-light" data-testid={`decor-include-${i}`}>
                  <Check className="w-4 h-4 accent-color mt-0.5 shrink-0" /> {inc}
                </div>
              ))}
            </div>
          )}
        </div>

        {addons.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-graphite font-medium mb-4">Make it yours · Add-on services</div>
            <div className="divide-y divide-ink/10 border-t border-b border-ink/10">
              {addons.map((a, i) => {
                const q = qty[i] || 0;
                return (
                  <div key={i} className={`flex items-center gap-4 py-4 transition-colors ${q > 0 ? "bg-bone/50 -mx-3 px-3" : ""}`} data-testid={`decor-addon-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm md:text-[15px] font-medium">{a.name}</div>
                      <div className="text-xs text-graphite mt-0.5">
                        {inr(a.price)}{a.unit ? ` ${a.unit}` : ""}{a.description ? ` · ${a.description}` : ""}
                      </div>
                    </div>
                    {q > 0 && <div className="text-sm font-medium whitespace-nowrap" data-testid={`decor-addon-amount-${i}`}>{inr(q * num(a.price))}</div>}
                    <div className="flex items-center border border-ink/15">
                      <button type="button" onClick={() => bump(i, -1)} disabled={q === 0} aria-label={`Remove one ${a.name}`} className="w-9 h-9 flex items-center justify-center hover:bg-ink hover:text-ivory transition-colors disabled:opacity-30" data-testid={`decor-addon-dec-${i}`}>
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-8 text-center text-sm font-medium" data-testid={`decor-addon-qty-${i}`}>{q}</span>
                      <button type="button" onClick={() => bump(i, 1)} aria-label={`Add one ${a.name}`} className="w-9 h-9 flex items-center justify-center hover:bg-ink hover:text-ivory transition-colors" data-testid={`decor-addon-inc-${i}`}>
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="lg:col-span-5 lg:sticky lg:top-28">
        <div className="bg-ink text-ivory p-7 md:p-9">
          <div className="text-[10px] uppercase tracking-[0.22em] text-ivory/50 font-medium">Your decor estimate</div>
          <div className="mt-5 space-y-2.5 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-ivory/70 font-light">{d.base_label || "Base package"}</span>
              <span>{inr(basePrice)}</span>
            </div>
            {selected.map((a, i) => (
              <div key={i} className="flex justify-between gap-4">
                <span className="text-ivory/70 font-light truncate">{a.name}{a.qty > 1 ? ` × ${a.qty}` : ""}</span>
                <span className="whitespace-nowrap">{inr(a.amount)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-ivory/15 mt-5 pt-5 flex items-end justify-between gap-4">
            <span className="text-[10px] uppercase tracking-[0.22em] text-ivory/50">Approximate total</span>
            <span className="font-display text-3xl md:text-4xl" data-testid="decor-total">{inr(total)}</span>
          </div>
          <button
            type="button"
            onClick={requestDownload}
            disabled={downloading}
            className="mt-7 w-full inline-flex items-center justify-center gap-2.5 bg-ivory text-ink px-8 py-4 text-xs uppercase tracking-[0.22em] font-medium hover:bg-[var(--prop-accent,#9E7B4F)] hover:text-white transition-colors disabled:opacity-60"
            data-testid="decor-download-pdf"
          >
            <FileDown className="w-4 h-4" /> {downloading ? "Preparing PDF…" : "Download decor quote (PDF)"}
          </button>
          {d.disclaimer && <p className="text-[11px] text-ivory/45 mt-4 leading-relaxed">{d.disclaimer}</p>}
        </div>
      </div>

      <GuestDetailsDialog
        open={guestOpen}
        onClose={() => setGuestOpen(false)}
        onSubmit={(g) => { setGuestOpen(false); downloadPdf(g); }}
        heading="Almost there — who is this decor quote for?"
        ctaLabel="Get my decor quote"
      />
    </div>
  );
}
